import { isNil } from 'lodash';
import { CSSProperties, useCallback } from 'react';
import { ScrollView } from 'react-native';
import { View } from '@shared/components/View';
import { SupportedSubformComponentValue } from '@shared/components/blocks/types';
import { MBModalOrMobile } from '@shared/components/modalOrMobile';
import { NavHeader } from '@shared/components/nav';
import { ResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { EditField } from '@shared/scenes/entity/components/fields/EditField';
import { SubformData } from '@shared/scenes/entity/components/fields/Subform/useSubformData';
import { getFieldError } from '@shared/scenes/entity/util/getFieldError';
import { PAGE_MODAL_WIDTH, grid } from '@shared/styles';
import { Colors } from '@shared/styles/colors';
import { isStaticTextComponent } from '../../utils';

interface Props extends SubformData {
  onSubmit: (params: {
    data: Record<string, SupportedSubformComponentValue | null>;
    wasValid: boolean;
  }) => void;
  isAuthenticated?: boolean;
  parentWorkflowTemplateId?: string;
  isReadOnly?: boolean;
}

export function SubformView({
  workflowTemplate,
  components,
  orderedComponents,
  fields,
  updateField,
  errors,
  isValid,
  isInitialized,
  setSubmitAttempted,
  onSubmit,
  isAuthenticated = true,
  parentWorkflowTemplateId,
  isReadOnly = false,
}: Props) {
  const handleBack = useCallback(() => {
    if (!isReadOnly) {
      setSubmitAttempted(true);
    }
    const payload: Record<string, SupportedSubformComponentValue | null> = {};
    for (const component of components) {
      if (isStaticTextComponent(component)) {
        continue;
      }
      const field = fields[component.id];
      if (!isNil(field) && !field.isEmpty) {
        payload[component.id] = field.toFieldJson();
      } else {
        payload[component.id] = null;
      }
    }
    onSubmit({ data: payload, wasValid: isReadOnly || isValid });
  }, [isValid, isReadOnly, components, fields, onSubmit, setSubmitAttempted]);

  return (
    <MBModalOrMobile
      desktopModalWidth={PAGE_MODAL_WIDTH}
      desktopModalHeight={'calc(95vh - 76px)'}
      title={workflowTemplate?.name}
      onBack={handleBack}
      paddingOnWeb={0}
      headerOnMobile={<NavHeader title={workflowTemplate?.name} onPressLeft={handleBack} />}
    >
      <ResponsiveFormContext.Provider value={true}>
        {/*
         * The gray background is on the ScrollView itself (not an inner wrapper)
         * so it fills the full scroll area and never scrolls away with content.
         * contentContainerStyle flexGrow:1 ensures it fills the visible modal
         * height even when there are few fields.
         */}
        <ScrollView
          style={scrollContainerStyle}
          contentContainerStyle={scrollContentStyle}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
        >
          <View style={fieldContainerStyle}>
            {isInitialized &&
              orderedComponents.map(component => (
                <EditField
                  key={component.id}
                  component={component}
                  field={fields[component.id]}
                  fields={fields}
                  updateField={updateField}
                  error={getFieldError(errors, component.id)}
                  isAuthenticated={isAuthenticated}
                  workflowTemplateId={
                    parentWorkflowTemplateId ??
                    workflowTemplate?.parentId ??
                    workflowTemplate?.id ??
                    ''
                  }
                  style={fieldStyle}
                />
              ))}
          </View>
        </ScrollView>
      </ResponsiveFormContext.Provider>
    </MBModalOrMobile>
  );
}

const scrollContainerStyle = {
  flex: 1,
  backgroundColor: Colors.v2.backgroundGrayLight,
  paddingTop: grid(1),
  paddingBottom: grid(4),
  borderTopWidth: 1,
  borderTopColor: Colors.v2.gray10,
};

const scrollContentStyle = {
  flexGrow: 1,
  paddingTop: grid(1),
  paddingBottom: grid(4),
  paddingLeft: grid(1),
  paddingRight: grid(1),
};

const fieldContainerStyle = {
  width: '100%',
  maxWidth: 456,
  alignSelf: 'center' as const,
};

// Matches EntityFormResponsive's formComponent + formComponentStandalone spacing.
const fieldStyle: CSSProperties = {
  paddingTop: grid(0.25),
  paddingBottom: grid(1.75),
  marginLeft: grid(2),
  marginRight: grid(2),
};
